import React from 'react';
import { X } from 'lucide-react';

interface RenameFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentName: string;
    onSubmit: (newName: string) => Promise<void>;
}

export const RenameFolderModal: React.FC<RenameFolderModalProps> = ({ isOpen, onClose, currentName, onSubmit }) => {
    const [name, setName] = React.useState(currentName);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        setName(currentName);
    }, [currentName]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (name.trim().length === 0) {
            setError('Nome da pasta não pode ser vazio');
            return;
        }

        if (name.length > 50) {
            setError('Nome da pasta deve ter no máximo 50 caracteres');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await onSubmit(name.trim());
            onClose();
        } catch (e: any) {
            setError(e.response?.data?.error || 'Erro ao renomear pasta');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                border: '1px solid #333',
                borderRadius: '16px',
                width: '90%',
                maxWidth: '450px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}>
                <div style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                        Renomear Pasta
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <X size={18} color="#fff" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>
                            Novo Nome
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={50}
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid #333',
                                borderRadius: '10px',
                                color: '#fff',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                        />
                        {error && (
                            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#ef4444' }}>
                                {error}
                            </p>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            style={{
                                padding: '12px 24px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid #333',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !name.trim()}
                            style={{
                                padding: '12px 24px',
                                background: loading || !name.trim() ? '#555' : 'var(--btn-gradient)',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: loading || !name.trim() ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {loading ? 'Renomeando...' : 'Renomear'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
